import { interpolate, Easing, spring } from "remotion";
import { FPS } from "@/components/editor/constants";

export type EasingOption = {
  name: string;
  easing?: (n: number) => number;
};

export const easingOptions: EasingOption[] = [
  {
    name: "None",
  },
  {
    name: "Linear",
    easing: Easing.linear,
  },
  {
    name: "Ease",
    easing: Easing.ease,
  },
  {
    name: "Bounce",
    easing: Easing.bounce,
  },
  {
    name: "Back",
    easing: Easing.back(1.7),
  },
  {
    name: "Quad",
    easing: Easing.quad
  },
  {
    name: "Cubic",
    easing: Easing.cubic,
  },
  {
    name: "Sin",
    easing: Easing.sin,
  },
  {
    name: "Circle",
    easing: Easing.circle,
  },
  {
    name: "Exp",
    easing: Easing.exp,
  },
  {
    name: "Elastic", // New easing option
    easing: Easing.elastic(1), // Example with a parameter
  },

];

export type AnimationTemplate = {
  name: string;
  preview: string;
  isPro?: boolean;
  enter?: (
    frame: number,
    durationInFrames: number,
    fps: number,
    properties?: {
      name?: string;
      easing?: string;
      durationInFrames?: number;
    }
  ) => {
    transform?: string;
    opacity?: number;
  };
  loop?: (
    frame: number,
    durationInFrames: number,
    fps: number,
    properties?: {
      name?: string;
      easing?: string;
      durationInFrames?: number;
    }
  ) => {
    transform?: string;
    opacity?: number;
  };
  exit?: (
    frame: number,
    durationInFrames: number,
    fps: number,
    properties?: {
      name?: string;
      easing?: string;
      durationInFrames?: number;
    }
  ) => {
    transform?: string;
    opacity?: number;
  };
};


export const animationTemplates: Record<string, AnimationTemplate> = {
  fade: {
    name: "Fade",
    preview: "Simple fade in/out",
    enter: (frame, duration, fps, properties) => ({
      opacity: interpolate(frame, [0, properties?.durationInFrames ?? 15], [0, 1], {
        extrapolateRight: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      }),
    }),
    loop: (frame, duration, fps, properties) => ({
      opacity: interpolate(frame % (properties?.durationInFrames ?? 15), [0, (properties?.durationInFrames ?? 15)/2, properties?.durationInFrames ?? 15], [0.5, 1, 0.5], {
        extrapolateRight: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      }),
    }),
    exit: (frame, duration, fps, properties) => ({
      opacity: interpolate(frame, [duration - (properties?.durationInFrames ?? 15), duration], [1, 0], {
        extrapolateLeft: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined,
      }),
    }),
  },
  slideRight: {
    name: "Slide",
    preview: "Slide in from left",
    isPro: true,
    enter: (frame, duration, fps, properties) => ({
      transform: `translateX(${interpolate(frame, [0, (properties?.durationInFrames ?? 15)], [-100, 0], {
        extrapolateRight: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      })}%)`,
      opacity: interpolate(frame, [0, (properties?.durationInFrames ?? 15)], [0, 1], {
        extrapolateRight: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      }),
    }),
    exit: (frame, duration, fps, properties) => ({
      transform: `translateX(${interpolate(frame,[duration - (properties?.durationInFrames ?? 15), duration],[0, 100],
        {
          extrapolateLeft: "clamp",
          easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
        }
      )}%)`,
      opacity: interpolate(frame, [duration - (properties?.durationInFrames ?? 15), duration], [1, 0], {
        extrapolateLeft: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      }),
    }),
  },
  scale: {
    name: "Scale",
    preview: "Scale in/out",
    enter: (frame, duration, fps, properties) => ({
      transform: `scale(${interpolate(frame, [0, properties?.durationInFrames ?? 15], [0, 1], {
        extrapolateRight: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      })})`,
      opacity: interpolate(frame, [0, properties?.durationInFrames ?? 15], [0, 1], {
        extrapolateRight: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      }),
    }),
    loop: (frame, duration, fps, properties) => ({
      transform: `scale(${interpolate(frame % (properties?.durationInFrames ?? 15), [0, (properties?.durationInFrames ?? 15)/2, properties?.durationInFrames ?? 15], [0.8, 1.1, 0.8], {
        extrapolateRight: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      })})`,
    }),
    exit: (frame, duration, fps, properties) => ({
      transform: `scale(${interpolate(frame,[duration - (properties?.durationInFrames ?? 15), duration],[1, 0],{ 
          extrapolateLeft: "clamp",
          easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
        }
      )})`,
      opacity: interpolate(frame, [duration - (properties?.durationInFrames ?? 15), duration], [1, 0], {
        extrapolateLeft: "clamp",
        easing: easingOptions.find((e) => e.name === properties?.easing)?.easing ?? undefined, 
      }),
    }),
  },
  spring: {
    name: "Spring",
    preview: "Spring in/out",
    enter: (frame, duration, fps, properties) => ({
      transform: `scale(${spring({frame, fps: fps??FPS, durationInFrames: properties?.durationInFrames})})`,
    }),
  },
};  
